/**
 * Mega Menu — mobile hamburger + drawer + accordion.
 *
 * Runs on the frontend only. Desktop dropdown behaviour is handled
 * by mega-menu-item/view.js.
 *
 * Strategy
 * ─────────
 * Rather than duplicating server-rendered HTML, we build the mobile
 * nav at runtime by reading the already-rendered desktop items. This
 * keeps the source HTML lean and avoids double-rendering inner blocks.
 *
 * The accordion panels clone the desktop .mega-menu__dropdown-inner
 * nodes, so any rich content (columns, images, nav links) is preserved.
 */

const nav        = document.querySelector( '.wp-block-jormi-blocks-mega-menu' );
if ( ! nav ) throw new Error( 'Mega menu root not found.' );

const hamburger  = nav.querySelector( '.mega-menu__hamburger' );
const modal      = nav.querySelector( '.mega-menu__mobile-modal' );
const drawer     = nav.querySelector( '.mega-menu__mobile-drawer' );
const closeBtn   = nav.querySelector( '.mega-menu__mobile-close' );
const mobileNav  = nav.querySelector( '.mega-menu__mobile-nav' );

// ── Build mobile nav ──────────────────────────────────────────────────────
//
// Called lazily on first open so there's zero cost on desktop viewports.
// Each desktop .wp-block-jormi-blocks-mega-menu-item becomes either:
//   • a plain link  (no dropdown)
//   • an accordion  (has dropdown — clones .mega-menu__dropdown-inner)

let mobileNavBuilt = false;

function buildMobileNav() {
    if ( mobileNavBuilt ) return;
    mobileNavBuilt = true;

    // Walk ALL direct children of .mega-menu__items, not just mega-menu-items.
    // This picks up core/buttons (or any other block) added alongside nav items.
    const itemsContainer = nav.querySelector( '.mega-menu__items' );
    if ( ! itemsContainer ) return;

    Array.from( itemsContainer.children ).forEach( ( child, i ) => {

        // ── Mega menu item (accordion or plain link) ──────────────────────
        if ( child.classList.contains( 'wp-block-jormi-blocks-mega-menu-item' ) ) {
            const item          = child;
            const link          = item.querySelector( '.mega-menu__link' );
            const dropdownInner = item.querySelector( '.mega-menu__dropdown-inner' );
            const labelEl       = item.querySelector( '.mega-menu__label' );
            const href          = link?.getAttribute( 'href' ) || '#';
            const newTab        = link?.getAttribute( 'target' ) === '_blank';
            const label         = labelEl?.innerHTML || '';

            const mobileItem    = document.createElement( 'div' );
            mobileItem.className = 'mega-menu__mobile-item';

            if ( dropdownInner ) {
                const toggleId = `mm-toggle-${ i }`;
                const panelId  = `mm-panel-${ i }`;

                const header = document.createElement( 'div' );
                header.className = 'mega-menu__mobile-item-header';
                header.innerHTML = `
                    <a
                        href="${ href }"
                        class="mega-menu__mobile-label"
                        ${ newTab ? 'target="_blank" rel="noopener noreferrer"' : '' }
                    >${ label }</a>
                    <button
                        class="mega-menu__mobile-toggle"
                        id="${ toggleId }"
                        aria-expanded="false"
                        aria-controls="${ panelId }"
                        aria-label="Toggle ${ labelEl?.textContent || '' } submenu"
                    >
                        <span class="mega-menu__chevron" aria-hidden="true"></span>
                    </button>
                `;

                const panel = document.createElement( 'div' );
                panel.className  = 'mega-menu__mobile-panel';
                panel.id         = panelId;
                panel.setAttribute( 'aria-labelledby', toggleId );
                panel.setAttribute( 'aria-hidden', 'true' );
                panel.appendChild( dropdownInner.cloneNode( true ) );

                const toggle = header.querySelector( '.mega-menu__mobile-toggle' );
                toggle.addEventListener( 'click', () => {
                    const isOpen = toggle.getAttribute( 'aria-expanded' ) === 'true';
                    if ( isOpen ) {
                        panel.style.maxHeight = `${ panel.scrollHeight }px`;
                        requestAnimationFrame( () => { panel.style.maxHeight = '0'; } );
                    } else {
                        panel.style.maxHeight = `${ panel.scrollHeight }px`;
                        panel.addEventListener(
                            'transitionend',
                            () => { panel.style.maxHeight = 'none'; },
                            { once: true }
                        );
                    }
                    toggle.setAttribute( 'aria-expanded', String( ! isOpen ) );
                    panel.setAttribute( 'aria-hidden',    String( isOpen ) );
                } );

                mobileItem.appendChild( header );
                mobileItem.appendChild( panel );

            } else {
                const a = document.createElement( 'a' );
                a.href      = href;
                a.className = 'mega-menu__mobile-label';
                a.innerHTML = label;
                if ( newTab ) { a.target = '_blank'; a.rel = 'noopener noreferrer'; }
                mobileItem.appendChild( a );
            }

            mobileNav.appendChild( mobileItem );

        // ── Any other block (buttons, etc.) — clone directly ─────────────
        } else {
            const wrapper = document.createElement( 'div' );
            wrapper.className = 'mega-menu__mobile-extra';
            wrapper.appendChild( child.cloneNode( true ) );
            mobileNav.appendChild( wrapper );
        }
    } );
}

// ── Scroll lock ───────────────────────────────────────────────────────────
//
// `overflow: hidden` on body alone doesn't prevent scroll on iOS Safari —
// the page can still drift underneath a fixed overlay. The fix is to make
// body position:fixed at the current scroll offset, which fully locks it.
// We restore the position (and scroll) on close so the viewport doesn't
// jump to the top.

let savedScrollY = 0;

function lockScroll() {
    savedScrollY = window.scrollY;
    document.body.style.overflow  = 'hidden';
    document.body.style.position  = 'fixed';
    document.body.style.top       = `-${ savedScrollY }px`;
    document.body.style.width     = '100%';
}

function unlockScroll() {
    document.body.style.overflow  = '';
    document.body.style.position  = '';
    document.body.style.top       = '';
    document.body.style.width     = '';
    window.scrollTo( 0, savedScrollY );
}

// ── Open / close helpers ──────────────────────────────────────────────────

function openModal() {
    buildMobileNav();
    lockScroll();

    modal.setAttribute( 'aria-hidden', 'false' );
    hamburger.setAttribute( 'aria-expanded', 'true' );
    document.body.classList.add( 'mega-menu-mobile-open' );

    requestAnimationFrame( () => closeBtn?.focus() );
}

function closeModal() {
    unlockScroll();

    modal.setAttribute( 'aria-hidden', 'true' );
    hamburger.setAttribute( 'aria-expanded', 'false' );
    document.body.classList.remove( 'mega-menu-mobile-open' );
    hamburger?.focus();
}

// ── Event bindings ────────────────────────────────────────────────────────

hamburger?.addEventListener( 'click', openModal );
closeBtn?.addEventListener( 'click', closeModal );

// Escape key
document.addEventListener( 'keydown', ( e ) => {
    if ( e.key === 'Escape' && modal?.getAttribute( 'aria-hidden' ) === 'false' ) {
        closeModal();
    }
} );

// Click on the backdrop (outside the drawer) closes the modal
modal?.addEventListener( 'click', ( e ) => {
    if ( ! drawer?.contains( e.target ) ) {
        closeModal();
    }
} );

// Close when a plain mobile link is tapped (no accordion) so the page
// navigates without the overlay hanging around on back-navigation
mobileNav?.addEventListener( 'click', ( e ) => {
    if ( e.target.closest( '.mega-menu__mobile-label' ) && ! e.target.closest( '.mega-menu__mobile-item-header' ) ) {
        closeModal();
    }
} );